import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface SavedSearch {
  id: number;
  user_id: string;
  search_name: string;
  description?: string;
  search_query: string;
  search_filters?: Record<string, any>;
  search_type: string;
  is_favorite: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  last_used?: string;
  use_count: number;
}

interface SaveSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (searchData: any) => void;
  initialData?: {
    query: string;
    filters?: Record<string, any>;
  };
}

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

const SaveSearchModal: React.FC<SaveSearchModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
  const [searchName, setSearchName] = useState('');
  const [description, setDescription] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSearchName('');
      setDescription('');
      setIsFavorite(false);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchName.trim()) return;

    onSave({
      search_name: searchName,
      description: description || undefined,
      search_query: initialData?.query || '',
      search_filters: initialData?.filters || null,
      is_favorite: isFavorite,
      search_type: 'opensearch'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Save Search</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Name *
            </label>
            <input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter a name for this search"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional description"
              rows={3}
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isFavorite"
              checked={isFavorite}
              onChange={(e) => setIsFavorite(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isFavorite" className="ml-2 block text-sm text-gray-900">
              Mark as favorite
            </label>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Save Search
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SavedSearches: React.FC = () => {
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchSavedSearches();
  }, []);

  const fetchSavedSearches = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/user/searches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSearches(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching saved searches:', err);
      setError(err.response?.data?.detail || 'Failed to load saved searches');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSearch = async (searchData: any) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      await axios.post(`${API_BASE_URL}/api/user/searches`, searchData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowModal(false);
      fetchSavedSearches(); // Refresh the list
    } catch (err: any) {
      console.error('Error saving search:', err);
      alert(err.response?.data?.detail || 'Failed to save search');
    }
  };

  const handleDeleteSearch = async (searchId: number) => {
    if (!window.confirm('Are you sure you want to delete this saved search?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      await axios.delete(`${API_BASE_URL}/api/user/searches/${searchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchSavedSearches(); // Refresh the list
    } catch (err: any) {
      console.error('Error deleting search:', err);
      alert(err.response?.data?.detail || 'Failed to delete search');
    }
  };

  const handleUseSearch = async (search: SavedSearch) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      // Record usage
      await axios.post(`${API_BASE_URL}/api/user/searches/${search.id}/use`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Navigate to search page with the saved query and filters
      const searchParams = new URLSearchParams({
        q: search.search_query,
        ...(search.search_filters && { filters: JSON.stringify(search.search_filters) })
      });
      
      window.location.href = `/search?${searchParams.toString()}`;
    } catch (err: any) {
      console.error('Error using saved search:', err);
      // Still navigate even if usage tracking fails
      const searchParams = new URLSearchParams({
        q: search.search_query,
        ...(search.search_filters && { filters: JSON.stringify(search.search_filters) })
      });
      window.location.href = `/search?${searchParams.toString()}`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '8rem'}}>
        <div style={{fontSize: '1.125rem', color: '#111827', fontWeight: 'bold'}}>Loading saved searches...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '0.5rem', padding: '1rem'}}>
        <div style={{color: '#991b1b', fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Error: {error}</div>
        <button 
          onClick={fetchSavedSearches}
          style={{marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: '#dc2626', color: 'white', borderRadius: '0.375rem', border: 'none', cursor: 'pointer', fontWeight: 'bold'}}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{backgroundColor: 'white', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderRadius: '0.5rem', border: '1px solid #e5e7eb'}}>
        <div style={{padding: '1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb'}}>
          <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827'}}>Saved Searches</h2>
          <button
            onClick={() => setShowModal(true)}
            style={{padding: '0.75rem 1rem', backgroundColor: '#2563eb', color: 'white', fontSize: '0.875rem', fontWeight: 'bold', borderRadius: '0.375rem', border: 'none', cursor: 'pointer'}}
          >
            Save Current Search
          </button>
        </div>
        
        {searches.length === 0 ? (
          <div style={{padding: '2rem', textAlign: 'center'}}>
            <div style={{color: '#111827', marginBottom: '1rem', fontSize: '1.125rem', fontWeight: 'bold'}}>No saved searches yet</div>
            <p style={{fontSize: '1rem', color: '#6b7280'}}>
              Save your frequently used searches for quick access later
            </p>
          </div>
        ) : (
          <div>
            {searches.map((search) => (
              <div key={search.id} style={{padding: '1.5rem', borderBottom: '1px solid #e5e7eb'}}>
                <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem'}}>
                      <h3 style={{fontSize: '1.125rem', fontWeight: 'bold', color: '#111827'}}>
                        {search.search_name}
                      </h3>
                      {search.is_favorite && (
                        <span style={{color: '#f59e0b', fontSize: '1.2rem'}}>★</span>
                      )}
                    </div>
                    {search.description && (
                      <p style={{fontSize: '1rem', color: '#4b5563', marginBottom: '0.75rem'}}>
                        {search.description}
                      </p>
                    )}
                    <div style={{marginBottom: '0.75rem'}}>
                      <span style={{backgroundColor: '#f3f4f6', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.875rem', fontFamily: 'monospace', color: '#111827', border: '1px solid #d1d5db'}}>
                        {search.search_query}
                      </span>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.875rem', color: '#6b7280'}}>
                      <span style={{fontWeight: '500'}}>Created: {formatDate(search.created_at)}</span>
                      {search.last_used && (
                        <span style={{fontWeight: '500'}}>Last used: {formatDate(search.last_used)}</span>
                      )}
                      <span style={{fontWeight: '500'}}>Used {search.use_count} times</span>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1rem'}}>
                    <button
                      onClick={() => handleUseSearch(search)}
                      style={{padding: '0.5rem 0.75rem', fontSize: '0.875rem', fontWeight: 'bold', color: '#1e40af', backgroundColor: '#dbeafe', borderRadius: '0.375rem', border: '1px solid #93c5fd', cursor: 'pointer'}}
                    >
                      Use
                    </button>
                    <button
                      onClick={() => handleDeleteSearch(search.id)}
                      style={{padding: '0.5rem 0.75rem', fontSize: '0.875rem', fontWeight: 'bold', color: '#991b1b', backgroundColor: '#fee2e2', borderRadius: '0.375rem', border: '1px solid #fca5a5', cursor: 'pointer'}}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <SaveSearchModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaveSearch}
        initialData={{ query: '', filters: {} }}
      />
    </div>
  );
};

export default SavedSearches;